import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ArrowRight, Loader2, Check } from 'lucide-react'
import Navbar from '@/components/layout/Navbar'
import Footer from '@/components/layout/Footer'
import { Label } from '@/components/ui/label'
import { useAuth } from '@/hooks/useAuth'
import { supabase } from '@/lib/supabaseClient'

const inputClass = 'w-full px-6 py-4 rounded-lg border border-white/20 bg-white/10 text-white placeholder:text-white/50 focus:outline-none hover:border-white/30 focus:border-white/40 transition-all'

export default function CompleteAccount() {
  const { user, loading } = useAuth()
  const navigate = useNavigate()
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [phone, setPhone] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState(false)

  useEffect(() => {
    if (user?.user_metadata?.first_name) {
      setFirstName(user.user_metadata.first_name)
    }
    if (user?.user_metadata?.last_name) {
      setLastName(user.user_metadata.last_name)
    }
  }, [user?.user_metadata?.first_name, user?.user_metadata?.last_name])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (firstName.trim().length < 2 || lastName.trim().length < 2) {
      setError('Please enter your first and last name.')
      return
    }
    if (password.trim().length < 6) {
      setError('Password must be at least 6 characters.')
      return
    }
    if (password !== confirmPassword) {
      setError("Passwords don't match.")
      return
    }

    setSubmitting(true)
    const marketingConsent = user?.user_metadata?.marketing_consent ?? false
    const { error: updateError } = await supabase.auth.updateUser({
      password: password.trim(),
      data: {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        full_name: `${firstName.trim()} ${lastName.trim()}`.trim(),
        phone: phone.trim() || undefined,
      },
    })
    if (updateError) {
      setSubmitting(false)
      setError(updateError.message)
      return
    }

    // Sync to people table (first_name, last_name, phone, marketing_consent)
    const { data: peopleRows } = await supabase
      .from('people')
      .select('id')
      .eq('auth_user_id', user!.id)
      .limit(1)
    if (peopleRows?.[0]) {
      await supabase
        .from('people')
        .update({
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          phone: phone.trim() || null,
          marketing_consent: marketingConsent,
          updated_at: new Date().toISOString(),
        })
        .eq('id', peopleRows[0].id)
    }
    setSubmitting(false)
    setDone(true)
  }

  return (
    <div className="min-h-screen flex flex-col bg-accent font-sans text-white selection:bg-white selection:text-accent">
      <Navbar />
      <main className="relative flex-1 pt-32 pb-24">
        <div className="absolute inset-0 bg-[url('/assets/bg-noise.png')] opacity-10 mix-blend-overlay pointer-events-none" />
        <div className="absolute inset-0 bg-gradient-to-b from-accent/80 via-transparent to-accent pointer-events-none" />

        <div className="container relative z-10 max-w-xl mx-auto px-8">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-8 w-8 animate-spin text-white/60" />
            </div>
          ) : done ? (
            <div className="text-center space-y-6 animate-fade-up">
              <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-white/20 text-white">
                <Check className="h-8 w-8" />
              </div>
              <h1 className="text-3xl font-bold font-heading text-white">Account completed</h1>
              <p className="text-slate-200 text-lg">
                Your account details are now set. You can continue using your client portal.
              </p>
              <div className="flex items-center justify-center gap-4">
                <button
                  type="button"
                  onClick={() => navigate('/new-project')}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-white text-accent font-medium hover:bg-white/90 transition-all"
                >
                  Back to project brief
                  <ArrowRight className="h-4 w-4" />
                </button>
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 text-white font-medium hover:underline"
                >
                  Back to home
                </Link>
              </div>
            </div>
          ) : !user ? (
            <div className="space-y-6 animate-fade-up">
              <h1 className="text-3xl font-bold font-heading text-white">Complete your account</h1>
              <p className="text-slate-200">
                Open the verification link from your email on this device to continue. If it expired, enter your email again on the homepage to receive a new link.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 text-white font-medium hover:underline"
                >
                  Back to homepage
                  <ArrowRight className="h-4 w-4" />
                </Link>
                <Link
                  to="/login"
                  className="inline-flex items-center gap-2 text-white/70 hover:text-white font-medium hover:underline"
                >
                  Go to login
                </Link>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6 animate-fade-up">
              <div>
                <h1 className="text-3xl font-bold font-heading text-white">Complete your account</h1>
                <p className="text-white/70 text-sm mt-2">
                  Add your name, phone number, and password so you can sign in anytime.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="complete-first-name" className="text-base font-medium text-white/90">
                    First name
                  </Label>
                  <input
                    id="complete-first-name"
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className={inputClass}
                    required
                    minLength={2}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="complete-last-name" className="text-base font-medium text-white/90">
                    Last name
                  </Label>
                  <input
                    id="complete-last-name"
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className={inputClass}
                    required
                    minLength={2}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="complete-phone" className="text-base font-medium text-white/90">
                  Phone number
                </Label>
                <input
                  id="complete-phone"
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className={inputClass}
                  placeholder="Optional"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="complete-password" className="text-base font-medium text-white/90">
                  Password
                </Label>
                <input
                  id="complete-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={inputClass}
                  placeholder="At least 6 characters"
                  required
                  minLength={6}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="complete-confirm-password" className="text-base font-medium text-white/90">
                  Confirm password
                </Label>
                <input
                  id="complete-confirm-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className={inputClass}
                  required
                  minLength={6}
                />
              </div>

              {error && <p className="text-sm text-red-300">{error}</p>}

              <button
                type="submit"
                disabled={submitting}
                className="w-full flex items-center justify-center gap-2 px-8 py-4 rounded-lg bg-white text-accent font-medium hover:bg-white/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    Complete account
                    <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </button>
            </form>
          )}
        </div>
      </main>
      <Footer />
    </div>
  )
}
